import { useCallback, useMemo, useEffect, useRef, useState } from "react"

import { cn } from "@/tiptap/lib/tiptap-utils"
import { useToc } from "@/tiptap/nodes/toc-node/context/toc-context"
import {
  findViewportActiveHeadingId,
  getTocHeadingElement,
  getTocScrollListenerTarget,
  getTocScrollRoot,
  isTocUserScrollKey,
} from "@/tiptap/nodes/toc-node/lib/toc-viewport"


export function TocSidebar({
  className,
  maxShowCount = 20,
  topOffset = 0,
  ...props
}) {
  const { tocContent, navigateToHeading, normalizeHeadingDepths } = useToc()

  // Manual active id (from user clicks). This should override scroll-based
  // and selection-based active states while it's set.
  const [manualActiveId, setManualActiveId] = useState(null)
  const [viewportActiveId, setViewportActiveId] = useState(null)

  const headingList = useMemo(() => tocContent ?? [], [tocContent])

  const visibleHeadings = useMemo(() => headingList.slice(0, maxShowCount), [headingList, maxShowCount])

  const normalizedDepths = useMemo(
    () => normalizeHeadingDepths(visibleHeadings),
    [visibleHeadings, normalizeHeadingDepths]
  )

  const depthById = useMemo(() => {
    const map = new Map()
    visibleHeadings.forEach((h, index) => {
      map.set(h.id, normalizedDepths[index] ?? 1)
    })
    return map
  }, [visibleHeadings, normalizedDepths])

  // Decide which heading is "active" for UI purposes
  const activeContentId = useMemo(() => {
    // 1. User clicked something: manual always wins.
    if (manualActiveId) {
      return manualActiveId
    }

    // 2. Otherwise, use the heading nearest to the current viewport position.
    if (viewportActiveId) {
      return viewportActiveId
    }

    // 3. Fallback to first heading.
    return headingList[0]?.id
  }, [manualActiveId, viewportActiveId, headingList])

  const handleContentClick = useCallback((e, item) => {
    e.preventDefault()
    e.stopPropagation()

    // Mark this heading as manually active
    setManualActiveId(item.id)
    navigateToHeading(item, { topOffset })
  }, [navigateToHeading, topOffset])

  const sidebarRef = useRef(null)

  // Keep a ref in sync so the scroll handler can read the latest value
  // without being recreated on every manualActiveId change.
  const manualActiveIdRef = useRef(manualActiveId)
  manualActiveIdRef.current = manualActiveId

  /**
   * Track the heading at the scrollport's reading line, and clear a
   * click-selected pip only when the user scrolls that same scrollport
   * (window on KB articles, the editor pane on form fields).
   */
  useEffect(() => {
    if (typeof window === "undefined" || !headingList.length) return

    const fromElement =
      headingList.map(getTocHeadingElement).find(Boolean) || sidebarRef.current

    const scrollRoot = getTocScrollRoot(fromElement)
    const scrollTarget = getTocScrollListenerTarget(scrollRoot)

    const updateViewportActiveId = () => {
      setViewportActiveId(
        findViewportActiveHeadingId(headingList, { topOffset, scrollRoot }),
      )
    }

    const handleUserScroll = () => {
      if (manualActiveIdRef.current !== null) {
        setManualActiveId(null)
      }
    }

    const handleUserScrollKey = (event) => {
      if (!isTocUserScrollKey(event)) return
      handleUserScroll()
    }

    updateViewportActiveId()
    scrollTarget.addEventListener("scroll", updateViewportActiveId, { passive: true })
    window.addEventListener("resize", updateViewportActiveId)
    scrollTarget.addEventListener("wheel", handleUserScroll, { passive: true })
    scrollTarget.addEventListener("touchmove", handleUserScroll, { passive: true })
    // Capture on window: after a TOC click, focus stays on the sibling nav, so
    // Page/arrow keys never reach an inner editor pane's listeners.
    window.addEventListener("keydown", handleUserScrollKey, true)

    return () => {
      scrollTarget.removeEventListener("scroll", updateViewportActiveId)
      window.removeEventListener("resize", updateViewportActiveId)
      scrollTarget.removeEventListener("wheel", handleUserScroll)
      scrollTarget.removeEventListener("touchmove", handleUserScroll)
      window.removeEventListener("keydown", handleUserScrollKey, true)
    }
  }, [headingList, topOffset])

  const hasHeadings = headingList.length > 0

  return (
    <div ref={sidebarRef} className={cn("toc-sidebar", className)} {...props}>
      <div className="toc-sidebar-wrapper" tabIndex={hasHeadings ? 0 : undefined}>
        <div className="toc-sidebar-inner">
          {/* Progress rail */}
          <div className="toc-sidebar-progress">
            {visibleHeadings.map((item) => {
              const depth = depthById.get(item.id) ?? 1
              const isActive = activeContentId === item.id

              return (
                <div
                  key={item.id}
                  className={cn(
                    "toc-sidebar-progress-line",
                    isActive && "toc-sidebar-progress-line--active"
                  )}
                  data-depth={depth}
                  style={{
                    "--toc-depth": depth
                  }} />
              );
            })}
          </div>

          {/* TOC nav */}
          <nav
            className={cn("toc-sidebar-nav", !hasHeadings && "toc-sidebar-nav--hidden")}
            aria-label="Table of contents">
            <div className="toc-sidebar-popover">
              {visibleHeadings.map((item) => {
                const depth = depthById.get(item.id) ?? 1
                const isActive = activeContentId === item.id

                return (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    rel="noopener noreferrer"
                    className={cn("toc-sidebar-item notranslate", isActive && "toc-sidebar-item--active")}
                    data-depth={depth}
                    style={{
                      "--toc-depth": depth
                    }}
                    onClick={(e) => handleContentClick(e, item)}
                    aria-current={isActive ? "location" : undefined}>
                    {item.textContent}
                  </a>
                );
              })}
            </div>
          </nav>
        </div>
      </div>
    </div>
  );
}
